import { useState, useEffect, useRef, useContext, RefObject } from 'react';
import { useNavigate } from 'react-router';
import { EditIcon, Trash, VerticalDots } from '../../../../assets/images';
import { truncateData } from '../../../../services';
import {
  createdBy,
  deleteText,
  editText,
  triggerType,
  viewDetails,
  workflowStatus,
} from '../data/constants';
import NewProjectModal from '../pages/automator-projects/add-edit-automator';
import DeleteWorkflowProject from '../pages/automator-projects/delete-automator';
import { existingProjectCardProps } from '../automator.props';
import { DisableWorkflowContext } from '../../../../utils/context';
import StyledTooltip from '../../../../components/tooltip';
import { DynamicStatusTag } from '../../../super-admin/dashboard/data/constant';

function ExistingProjectCard({
  project,
  setDataUpdated,
  isEditAllowed,
  isDeleteAllowed,
}: existingProjectCardProps) {
  const navigate = useNavigate();
  const { setDisableMode } = useContext(DisableWorkflowContext);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [newProjectModal, setNewProjectModal] = useState<boolean>(false);
  const [deleteProject, setDeleteProject] = useState<boolean>(false);

  const [dataToDelete, setDataToDelete] = useState<number | null>(null);
  const dropdownRef: RefObject<HTMLDivElement> = useRef(null); // Ref for the dropdown container

  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleEditClick = () => {
    setNewProjectModal(true);
    setIsDropdownOpen(false);
    localStorage.removeItem('disableMode');
    setDisableMode(false);
  };

  const handleDeleteClick = (id: number) => {
    setDeleteProject(true);
    setIsDropdownOpen(false);
    setDataToDelete(id);
  };

  const handleDetailsClick = (projectDetails: { id: number; workflow_id?: number }) => {
    navigate(`/automator/workflow/${projectDetails.id}/${projectDetails.workflow_id}`);
    localStorage.setItem('disableMode', 'true');
    setDisableMode(true);
  };

  return (
    <div>
      <div className="flex flex-col border px-4 py-3 rounded-lg items-start gap-2  mt-2 min-h-[220px]">
        <div className="flex justify-between items-center w-full">
          <StyledTooltip title={project?.name}>
            <span className="text-[#EF7707] text-xl font-bold">
              {truncateData(project?.name, 18)}
            </span>
          </StyledTooltip>
          <div className="relative" ref={dropdownRef}>
            <button type="button" className="text-gray-600" onClick={toggleDropdown}>
              <img src={VerticalDots} className="pt-2" alt="action dots" />
            </button>
            {isDropdownOpen && (
              <div className="absolute right-0 w-44 bg-white border rounded-lg shadow-lg">
                {isEditAllowed && (
                  <button
                    type="button"
                    className="flex items-center w-full pl-2 py-2 gap-2 border-b-2"
                    onClick={handleEditClick}
                  >
                    <img src={EditIcon} height={15} width={15} alt="pencil icon" />
                    <span className="text-sm">{editText}</span>
                  </button>
                )}
                {isDeleteAllowed && (
                  <button
                    type="button"
                    className="flex items-center w-full pl-2 py-2 gap-2 border-b-2"
                    onClick={() => handleDeleteClick(project.id)}
                  >
                    <img src={Trash} height={15} width={15} alt="delete" />
                    <span className="text-sm">{deleteText}</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <span className="bg-[#F3F3F3] text-[#6A6A6A] text-xs px-2 py-1 rounded-xl">
          {createdBy} {project?.username}
        </span>

        <div className="flex flex-wrap gap-2">
          <span className="bg-[#F3F3F3] text-[#6A6A6A] text-xs px-2 pt-2 py-1 rounded-xl">
            {triggerType} <span className="font-extrabold">{project?.workflow_trigger_type}</span>
          </span>
          <span className="text-[#6A6A6A] text-xs px-2 py-1">
            {workflowStatus}{' '}
            <span className="ml-2">
              <DynamicStatusTag status={project?.workflow_status} />
            </span>
          </span>
        </div>
        <span className="text-[#232D42] text-[16px] min-h-[50px]">
          {truncateData(project?.description, 80)}
        </span>
        <button
          type="button"
          className="border px-3 py-1 rounded-lg my-2 text-[#6A6A6A]"
          onClick={() => handleDetailsClick(project)}
        >
          {viewDetails}
        </button>
      </div>

      <NewProjectModal
        open={newProjectModal}
        setNewProjectModal={setNewProjectModal}
        data={project}
        setDataUpdated={setDataUpdated}
      />

      <DeleteWorkflowProject
        open={deleteProject}
        setDeleteWorkFlowProModalOpen={setDeleteProject}
        dataToDelete={dataToDelete}
        setDataToDelete={setDataToDelete}
        setDataUpdated={setDataUpdated}
      />
    </div>
  );
}

export default ExistingProjectCard;
